import { useEffect, useRef, useState } from 'react';
import { PROFILE_IDS, type ProfileId, type TranslationMode } from '@cixing/shared';
import { fetchProfiles, fetchQaStatus } from '../api.js';
import {
  AudioCapture,
  listMicrophones,
  requestMicPermission,
  type MicDevice,
} from '../audio/capture.js';
import { LevelMeter } from '../components/LevelMeter.js';
import { getDeviceIdentity, saveDeviceName } from '../device.js';

export interface SetupResult {
  profiles: ProfileId[];
  deviceId: string | undefined;
  noiseSuppression: boolean;
  mode: TranslationMode;
  /** 小声ブースト(入力ゲイン)。1 = なし。 */
  gain: number;
  /** 端末の永続ID(マイクの deviceId とは別物)。講義メモの持ち主識別用。 */
  ownDeviceId: string;
  deviceName: string;
}

/**
 * 開始前の設定画面。
 *
 * 用語集は**全件を常時適用**する。プロファイル選択は置かない —
 * 利用者は「今日の講義がどの分野か」を事前に知らないため、選択式は
 * 使われないか、誤った絞り込みで精度を落とすだけだった(現場方針)。
 * プロファイルはサーバー内部と管理画面の整理用としてのみ残っている。
 */
export function Setup({
  onStart,
  onOpenLectures,
  onOpenDocsQa,
}: {
  onStart: (result: SetupResult) => void;
  onOpenLectures: () => void;
  onOpenDocsQa: () => void;
}): JSX.Element {
  const [termCount, setTermCount] = useState<number>();
  /**
   * 資料Q&Aが使えるときだけ入口を出す。
   * サーバー側で無効(QA_ENABLED=false や APIキー未設定)のときにボタンだけ見えると、
   * 押した人が不具合と受け取る。取得に失敗した場合も出さない方に倒す。
   */
  const [qaAvailable, setQaAvailable] = useState(false);
  const [devices, setDevices] = useState<MicDevice[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [mode, setMode] = useState<TranslationMode>('speed');
  const [gain, setGain] = useState(1);
  const [ownDeviceId, setOwnDeviceId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [micGranted, setMicGranted] = useState(false);
  const [error, setError] = useState<string>();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string>();

  const levelRef = useRef({ rms: 0, tooQuiet: false, clipping: false });
  const captureRef = useRef<AudioCapture | null>(null);
  // マイクテスト中の観測値。React state に入れず ref で集計する。
  const testStatsRef = useRef({ peak: 0, sum: 0, count: 0, clipped: false });

  // 前回選んだモードを復元する(講義ごとに選び直させない)。
  useEffect(() => {
    void import('../db/idb.js').then(({ loadSetting }) =>
      loadSetting<TranslationMode>('translation-mode').then((saved) => {
        if (saved === 'speed' || saved === 'accuracy' || saved === 'hybrid') setMode(saved);
      }),
    );
    void import('../db/idb.js').then(({ loadSetting }) =>
      loadSetting<number>('mic-gain').then((saved) => {
        if (saved === 1 || saved === 2.5 || saved === 5 || saved === 10) setGain(saved);
      }),
    );
    void getDeviceIdentity().then((identity) => {
      setOwnDeviceId(identity.deviceId);
      setDeviceName(identity.deviceName);
    });
  }, []);

  useEffect(() => {
    fetchProfiles()
      .then((data) => setTermCount(data.enabled))
      .catch(() => {
        // 件数表示は補助情報。取得失敗で開始を妨げない。
        setTermCount(undefined);
      });

    fetchQaStatus()
      .then((status) => setQaAvailable(status.available))
      .catch(() => setQaAvailable(false));
  }, []);

  // 画面を離れるときにマイクを必ず解放する。掴んだままだと他アプリが使えない。
  useEffect(() => {
    return () => {
      void captureRef.current?.stop();
    };
  }, []);

  const grantMic = async (): Promise<void> => {
    setError(undefined);
    const result = await requestMicPermission();
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setMicGranted(true);
    // ラベルは権限取得後でないと空文字になるため、ここで初めて一覧を取る。
    const list = await listMicrophones();
    setDevices(list);
    if (list[0] && deviceId === '') setDeviceId(list[0].deviceId);
  };

  const runMicTest = async (): Promise<void> => {
    if (testing) {
      await stopMicTest();
      return;
    }

    setTestResult(undefined);
    testStatsRef.current = { peak: 0, sum: 0, count: 0, clipped: false };

    const capture = new AudioCapture();
    captureRef.current = capture;

    try {
      await capture.start(
        { deviceId: deviceId || undefined, noiseSuppression, gain },
        {
          onFrame: () => {
            // テスト中は送信しない。音量を見るだけ。
          },
          onLevel: (level) => {
            levelRef.current = level;
            const stats = testStatsRef.current;
            stats.peak = Math.max(stats.peak, level.peak);
            stats.sum += level.rms;
            stats.count += 1;
            if (level.clipping) stats.clipped = true;
          },
          onError: (message) => setError(message),
        },
      );
      setTesting(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'マイクを開始できませんでした。');
    }
  };

  const stopMicTest = async (): Promise<void> => {
    await captureRef.current?.stop();
    captureRef.current = null;
    setTesting(false);
    levelRef.current = { rms: 0, tooQuiet: false, clipping: false };

    const { peak, sum, count, clipped } = testStatsRef.current;
    if (count === 0) return;

    const avg = sum / count;
    // 判定のしきい値は現場での実測に基づく目安。厳しすぎると常に警告が出る。
    if (clipped || peak >= 0.98) {
      setTestResult('音が大きすぎて割れています。マイクを講師から少し離してください。');
    } else if (avg < 0.01) {
      setTestResult(
        '音量が不足しています。マイクを講師に近づけるか、外部マイクの使用を検討してください。',
      );
    } else {
      setTestResult('音量は良好です。');
    }
  };

  const handleStart = async (): Promise<void> => {
    await stopMicTest();
    void import('../db/idb.js').then(({ saveSetting }) => {
      void saveSetting('translation-mode', mode);
      void saveSetting('mic-gain', gain);
    });
    void saveDeviceName(deviceName);
    onStart({
      // 全プロファイルを常時適用する。
      profiles: [...PROFILE_IDS],
      deviceId: deviceId || undefined,
      noiseSuppression,
      mode,
      gain,
      ownDeviceId,
      deviceName: deviceName.trim(),
    });
  };

  return (
    <div className="screen">
      <div className="row">
        <h1 className="app-title" style={{ flex: 1 }}>
          CIXING リアルタイム通訳
        </h1>
        {qaAvailable && <button onClick={onOpenDocsQa}>資料に質問</button>}
        <button onClick={onOpenLectures}>講義メモ</button>
      </div>
      <p className="app-subtitle">
        中国語の講義を日本語字幕で表示します
        {termCount !== undefined && ` ・ 編み機用語 ${termCount} 語を自動適用`}
      </p>

      {error && <div className="notice error">{error}</div>}

      <div className="field">
        <label htmlFor="device-name">名前(講義メモの持ち主として表示されます)</label>
        <input
          id="device-name"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder="例: 田中"
          maxLength={30}
        />
      </div>

      <div className="notice">
        <strong>精度を上げるために</strong>
        <br />
        講師の近くにマイクを置くのが最も効果があります。編み機の稼働音が大きい場所では、
        スマートフォンを机に置くより、講師の近くに置いた外部マイクやワイヤレスマイクの方が
        はるかに良い結果になります。
      </div>

      <div className="field">
        <label>マイクの準備</label>
        {!micGranted ? (
          <button onClick={() => void grantMic()}>マイクの使用を許可する</button>
        ) : (
          <>
            <select
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              disabled={testing}
            >
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </option>
              ))}
            </select>
            <div className="hint">外部マイク・Bluetooth マイクもここに表示されます。</div>

            <div className="row" style={{ marginTop: 10 }}>
              <button onClick={() => void runMicTest()}>
                {testing ? 'テストを終了' : 'マイクをテスト'}
              </button>
              <div className="spacer" style={{ minWidth: 100 }}>
                <LevelMeter source={levelRef} />
              </div>
            </div>

            {testing && (
              <div className="hint">
                講師の声と同じくらいの大きさで話してみてください。数秒後に「テストを終了」を押すと
                判定が出ます。
              </div>
            )}
            {testResult && <div className="notice">{testResult}</div>}

            <label className="profile-item" style={{ marginTop: 10 }}>
              <input
                type="checkbox"
                checked={noiseSuppression}
                onChange={(e) => setNoiseSuppression(e.target.checked)}
              />
              <span>ノイズ抑制を使う</span>
            </label>
            <div className="hint">
              工場の騒音下では有効にしてください。声が途切れて聞き取られない場合は切ってみてください。
            </div>

            <div style={{ marginTop: 10 }}>
              <label className="hint" style={{ display: 'block', marginBottom: 4 }}>
                小声ブースト(講師の声が小さくて拾えないときに)
              </label>
              <div className="row">
                {[
                  { value: 1, label: 'なし' },
                  { value: 2.5, label: '中' },
                  { value: 5, label: '強' },
                  { value: 10, label: '最強' },
                ].map((o) => (
                  <button
                    key={o.value}
                    className={gain === o.value ? 'primary' : ''}
                    style={{ flex: 1 }}
                    onClick={() => setGain(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="hint">
                「マイクをテスト」で講師の声に合わせて音量バーが緑に振れる強さを選んでください。
                周囲の雑音も一緒に大きくなるため、必要なときだけ使うのがコツです。
              </div>
            </div>
          </>
        )}
      </div>

      <div className="field">
        <label>翻訳モード(あとで停止して選び直せます)</label>
        <div className="profile-list">
          <label className="profile-item">
            <input
              type="radio"
              name="mode"
              checked={mode === 'hybrid'}
              onChange={() => setMode('hybrid')}
            />
            <span>
              <strong>ハイブリッド(新・おすすめ)</strong>
              <span className="hint" style={{ display: 'block', marginTop: 2 }}>
                字幕は即表示、数秒後に専門用語辞書で訳し直した高精度版に自動で置き換わる
              </span>
            </span>
          </label>
          <label className="profile-item">
            <input
              type="radio"
              name="mode"
              checked={mode === 'speed'}
              onChange={() => setMode('speed')}
            />
            <span>
              <strong>スピード優先</strong>
              <span className="hint" style={{ display: 'block', marginTop: 2 }}>
                話すそばから字幕が出る。静かな教室向け
              </span>
            </span>
          </label>
          <label className="profile-item">
            <input
              type="radio"
              name="mode"
              checked={mode === 'accuracy'}
              onChange={() => setMode('accuracy')}
            />
            <span>
              <strong>精度優先</strong>
              <span className="hint" style={{ display: 'block', marginTop: 2 }}>
                専門用語と騒音に強い。工場内・機械稼働中向け(表示まで+1〜2秒)
              </span>
            </span>
          </label>
        </div>
      </div>

      <button
        className="primary start-button"
        disabled={!micGranted}
        onClick={() => void handleStart()}
      >
        翻訳を開始
      </button>
      {!micGranted && <div className="hint">先にマイクの使用を許可してください。</div>}
    </div>
  );
}
